import type { StaticDataRouteOption } from '@tanstack/react-router';

/**
 * The active feature comes from the route, as in Angular (`data.featureKey`, read by
 * `ActiveFeatureService`). There is no feature registry: a route declares its key next to its
 * component, and that declaration is the only one.
 *
 * ```ts
 * export const Route = createFileRoute('/_app/leads')({
 *   staticData: { featureKey: 'leads' },
 *   component: LeadsPage,
 * });
 * ```
 */
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /**
     * The backend feature key this page renders (backend types/feature.types.ts): which
     * configuration `/config/resolve` is asked for. Set it on the route that owns the feature;
     * child routes inherit it unless they declare their own.
     */
    featureKey?: string;
  }
}

/** The deepest declared feature key among the matched routes, or null. */
export function featureKeyFromMatches(
  matches: readonly { staticData?: StaticDataRouteOption }[],
): string | null {
  for (let i = matches.length - 1; i >= 0; i--) {
    const key = matches[i]?.staticData?.featureKey;
    if (typeof key === 'string' && key.length > 0) {
      return key;
    }
  }
  return null;
}
